import { lockFormDuringUpload } from "./active_storage/form_upload_lock"
import { Controller } from "@hotwired/stimulus"
import {
  applyPdfPageCount,
  directUpload,
  updateUploadedFile,
} from "./active_storage/upload_helpers"

// Single-file compact upload (nested form rows, replace-in-place fields).
export default class extends Controller {
  static targets = ["input", "hiddenInput", "button", "linkContainer", "status", "pdfPages"]
  static values = {
    url: String,
  }

  connect() {
    if (!this.hasInputTarget) return

    this.uploading = false
    this.inputTarget.disabled = false
    this.lifecycle = new AbortController()
    this.inputTarget.addEventListener("change", event => this.handleFileSelect(event), { signal: this.lifecycle.signal })
  }

  disconnect() {
    this.lifecycle?.abort()
    this.releaseUploadLock?.()
    clearTimeout(this.statusTimer)
  }

  handleFileSelect(event) {
    const file = event.target.files[0]
    if (!file) return

    this.uploadFile(file)
  }

  async uploadFile(file) {
    if (!this.hasHiddenInputTarget || this.uploading) return
    const signal = this.lifecycle.signal
    this.uploading = true
    this.inputTarget.disabled = true
    clearTimeout(this.statusTimer)
    const releaseUploadLock = lockFormDuringUpload(this.element)
    this.releaseUploadLock = releaseUploadLock

    const delegate = {
      directUploadWillStoreFileWithXHR: (request) => {
        if (signal.aborted) return
        this.showStatus("Uploading...")
        this.hideLinkContainer()

        request.upload.addEventListener("progress", (event) => {
          if (signal.aborted) return
          const progress = Math.round((event.loaded / event.total) * 100)
          this.showStatus(`Uploading ${progress}%`)
        })
      },
    }

    try {
      const blob = await directUpload(file, this.urlValue, delegate)
      if (signal.aborted) return
      updateUploadedFile(this.element, blob)
      this.dispatch("uploaded", { detail: { file } })

      this.showStatus("Upload complete!")
      this.showLinkContainer()

      if (this.hasPdfPagesTarget) await applyPdfPageCount(blob.signed_id, file, this.element)
      if (signal.aborted) return

      this.statusTimer = setTimeout(() => {
        this.hideStatus()
        this.showLinkContainer()
      }, 2000)
    } catch (error) {
      if (signal.aborted) return
      this.showStatus("Upload failed.")
      console.error(error)
      this.statusTimer = setTimeout(() => {
        this.hideStatus()
        this.showLinkContainer()
      }, 2000)
    } finally {
      releaseUploadLock()
      if (!signal.aborted) {
        this.uploading = false
        this.inputTarget.disabled = false
        this.inputTarget.value = ""
      }
    }
  }

  showStatus(message) {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = message
    this.statusTarget.classList.remove("hidden")
  }

  hideStatus() {
    if (!this.hasStatusTarget) return

    this.statusTarget.classList.add("hidden")
  }

  hideLinkContainer() {
    if (this.hasLinkContainerTarget) {
      this.linkContainerTarget.classList.add("hidden")
    }
  }

  showLinkContainer() {
    if (this.hasLinkContainerTarget) {
      this.linkContainerTarget.classList.remove("hidden")
    }
  }
}
